import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { State } from '@app/app.reducer';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppConstants } from '@shared/app-constants';
import { consoleLog } from '@shared/app-utils';
import { StatusBarService } from '@shared/status-bar/status-bar.service';
import { SetSpinnerValue } from '@store/helpers/helper.actions';
import { BehaviorSubject, throwError } from 'rxjs';

export interface TranslatedErrorMessage {
  status: number;
  key: string;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlingService {
  translatedErrorMessage = new BehaviorSubject<TranslatedErrorMessage>(null);

  constructor(
    private statusBarService: StatusBarService,
    private translateService: TranslateService,
    private store: Store<State>
  ) {}

  errorHandler(error: any): any {
    consoleLog(JSON.stringify(error));

    let errMsg: string;

    if (error instanceof HttpErrorResponse || (Object.hasOwn(error, 'errors') && error.errors.length > 0)) {
      const errorCode: string[] = [];
      let errorKeyExists = false;
      try {
        if (Object.hasOwn(error.error, 'errorCode')) {
          errorCode.push(error.error.errorCode);
        } else if (Object.hasOwn(error.error, 'ErrorCode')) {
          errorCode.push(error.error.ErrorCode);
        } else if (Object.hasOwn(error, 'errors')) {
          if (error.errors) {
            error.errors.forEach((element, index) => {
              errorCode.push(element.code);
              if (!errorKeyExists && index > 0) {
                errorKeyExists = errorCode.some((item) => item === element.code);
              }
            });
          }
        }

        if (!!errorCode && errorCode.length > 0) {
          errorCode.forEach((currentError, index) => {
            this.translateService
              // multipleErrors is key when there are multiple same error keys in errorCode array, else take currentError key and get it's translation if available
              .get('response.' + (errorKeyExists ? 'multipleErrors' : currentError))
              .subscribe((translation: string) => {
                errMsg = translation;

                if (Object.hasOwn(error.error, 'invalidFields')) {
                  const fields = error.error['invalidFields'].join(', ');
                  translation += `: ${fields}`;
                }

                // in case the error translation was not found.. respond with backend message
                if (
                  // if errors are in response object and there are multiple errors with same error code set errors to their message in error.errors array
                  translation === AppConstants.MISSING_TRANSLATION &&
                  !!error.errors &&
                  error.errors.length > 1
                ) {
                  translation = error.errors[index].message;
                } else if (
                  translation === AppConstants.MISSING_TRANSLATION &&
                  Object.hasOwn(error.error, 'errorMessage')
                ) {
                  translation = error.error.errorMessage;
                } else if (
                  translation === AppConstants.MISSING_TRANSLATION &&
                  Object.hasOwn(error.error, 'ErrorMessage')
                ) {
                  translation = error.error.ErrorMessage;
                } else if (translation === AppConstants.MISSING_TRANSLATION) {
                  translation = 'Server Error';
                }

                const translatedMessage = {
                  status: error.status,
                  key: currentError,
                  message: translation
                };

                this.translatedErrorMessage.next(translatedMessage);

                const isMissingSeries =
                  translation.startsWith('Event Serie with identifier') && translation.endsWith('does not exists.');

                if (!isMissingSeries) {
                  this.statusBarService.setStatus(translation, 'error');
                }
              });
          });
        }
      } catch (err) {
        errMsg = `${error.status} - ${error.statusText || ''} ${err}`;

        this.statusBarService.setStatus(errMsg, 'error');
      }
    } else {
      errMsg = error.message ? error.message : error.toString();

      this.statusBarService.setStatus(errMsg, 'error');
    }

    this.store.dispatch(new SetSpinnerValue(false));
    return throwError(() => new Error(errMsg));
  }

  clearAllErrors() {
    this.translatedErrorMessage.next(null);
  }
}
